/**
 * Validate-then-apply wrapper for chess actions.
 *
 * Composes validators + appliers so callers get a single safe entry point.
 * Appliers remain pure transformers; validation happens here.
 */
import { applyMove } from "./appliers/move.applier";
import { applySelectAugmentation } from "./appliers/select-augmentation.applier";
import type { ChessAction } from "./types/actions";
import type { ChessState } from "./types/game-state";
import { validateMove } from "./validators/move.validator";
import { validateSelectAugmentation } from "./validators/select-augmentation.validator";

/** Result of tryApplyAction */
export type ApplyActionResult =
  | { readonly success: true; readonly state: ChessState }
  | {
      readonly success: false;
      readonly error: string;
      readonly details?: Record<string, unknown>;
    };

/**
 * Validate the action against the current state, then apply if valid.
 *
 * - MOVE: validateMove → applyMove
 * - SELECT_AUGMENTATION: validateSelectAugmentation → applySelectAugmentation
 * - RESIGN: always succeeds (state unchanged)
 * - Unknown: returns failure
 */
export function tryApplyAction(
  state: ChessState,
  action: ChessAction,
): ApplyActionResult {
  switch (action.type) {
    case "MOVE": {
      const validation = validateMove(state, action);
      if (!validation.valid) {
        return {
          success: false,
          error: validation.error ?? "Invalid move",
          details: validation.details,
        };
      }
      return { success: true, state: applyMove(state, action) };
    }

    case "SELECT_AUGMENTATION": {
      const validation = validateSelectAugmentation(state, action);
      if (!validation.valid) {
        return {
          success: false,
          error: validation.error ?? "Invalid augmentation selection",
          details: validation.details,
        };
      }
      return { success: true, state: applySelectAugmentation(state, action) };
    }

    case "RESIGN": {
      return { success: true, state };
    }

    default: {
      return {
        success: false,
        error: "Unknown action type",
        details: { type: (action as { type: string }).type },
      };
    }
  }
}
